import _ from 'lodash';
import moment from 'moment';
import type { AnyProperty } from '@/utilities.types';
import type { ContextSchema } from '@/vantage/types/Context.types';
import { constructKey, valueOrDefault } from './utilities';
import { VantageNames } from '@/vantage/constants';
import type { ValueGetterFunc, ValueGetterParams } from 'ag-grid-community';

export const referenceValueGetter = (reference: string | undefined): ValueGetterFunc<any, any> => {
  return (params) => {
    try {
      if (reference && params?.data?.[reference!]) return params?.data?.[reference];
      return `**No value in referenced column ${reference}`;
    } catch (err) {
      console.error(`Error in accessing ${reference}:`, err);
    }
  };
};

export const whiteSpaceRemoverValueGetter = (): ValueGetterFunc<any, any> => {
  return (params) => {
    try {
      if (params?.data && params?.column?.getColId()) {
        const colId = params.column.getColId();
        if (params.data?.[colId]) {
          const value = params.data[colId];
          if (typeof value === 'string') {
            return value.replace(/^\s+/g, '');
          } else return value;
        } else return '';
      }
    } catch (err) {
      console.error(`Error in running whiteSpaceRemover valueGetter for column ${params?.column?.getColId()}:`, err);
    }
  };
};

// A known issue, it seems, without a fix (https://github.com/ag-grid/ag-grid/issues/2737)
export const possiblyZeroValueGetter = (column: string | undefined): ValueGetterFunc<any, any> => {
  return (params) => {
    const value = params?.node?.data?.[column!];
    return _.isNumber(value) && value === 0 ? '0' : value;
  };
};

export const contextFromMap = (
  evidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
  identifier: string | undefined,
  kindId: string,
) => evidenceContextMap.current.get(constructKey(kindId, identifier ?? '')) || [];

export const transformationValueGetter = (
  defaultValue: any,
  evidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
  identifier: string | undefined,
  transformation: (contextArrayOfInterest: ContextSchema[]) => any,
): ValueGetterFunc<any, any> => {
  return (params) => {
    const contexts = contextFromMap(evidenceContextMap, identifier, params?.data?.[VantageNames.Columns.CapsuleID]);
    return valueOrDefault(defaultValue, transformation, contexts);
  };
};

export const booleanValueGetter = (
  defaultValue: boolean,
  evidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
  identifier: string | undefined,
): ValueGetterFunc<any, boolean> => {
  return (params) => {
    const contexts = contextFromMap(evidenceContextMap, identifier, params?.data?.[VantageNames.Columns.CapsuleID]);
    return !!contexts.length || defaultValue;
  };
};

const now = moment();

interface ExpiredForecastConfig {
  creationColumn: string;
  startColumn: string;
  olderThan: { hours: number };
}

interface ReviewedStateConfig {
  identifier: string;
  createdField: keyof ContextSchema;
  updatedColumn: string;
}

const valueGetters: AnyProperty = {
  expiredForecast: (
    { creationColumn, startColumn, olderThan: { hours } }: ExpiredForecastConfig,
    evidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
    { data }: ValueGetterParams<AnyProperty>,
  ) => data && data[creationColumn] < data[startColumn] && now.diff(data[creationColumn], 'hours') > hours,
  reviewedState: (
    { identifier, createdField, updatedColumn }: ReviewedStateConfig,
    evidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
    { data }: ValueGetterParams<AnyProperty>,
  ) => {
    const contexts = contextFromMap(evidenceContextMap, identifier, data?.[VantageNames.Columns.CapsuleID]);
    const hasContext = contexts.length > 0;

    return hasContext && data && contexts.every((context) => context[createdField] < data[updatedColumn])
      ? 'stale'
      : hasContext;
  },
};

export const configValueGetter = (
  config: AnyProperty,
  evidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
): ValueGetterFunc => {
  const [key, value] = Object.entries(config)[0];
  const valueGetter = valueGetters[key];
  return (params) => valueGetter(value, evidenceContextMap, params);
};
